import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import ConfigService from './configService';
import getSupabaseClient from './supabaseService';
import {GameState} from '../models/GameState';

class ApiService {
  private client: AxiosInstance;
  private initialized = false;

  constructor() {
    this.client = axios.create({
      baseURL: ConfigService.apiBaseUrl,
      timeout: ConfigService.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor pour ajouter le token d'authentification
    this.client.interceptors.request.use(
      async (config) => {
        const supabase = getSupabaseClient();
        const session = await supabase.auth.getSession();
        
        if (session.data.session?.access_token) {
          config.headers.Authorization = `Bearer ${session.data.session.access_token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor pour gérer les erreurs
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Token expiré, essayer de rafraîchir
          const supabase = getSupabaseClient();
          const {data, error: refreshError} = await supabase.auth.refreshSession();
          
          if (!refreshError && data.session) {
            // Réessayer la requête avec le nouveau token
            error.config.headers.Authorization = `Bearer ${data.session.access_token}`;
            return this.client.request(error.config);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Récupère les gameStates modifiés depuis une date
   * Conforme à la documentation : TOUJOURS utiliser le paramètre since pour optimiser les requêtes
   * Si since n'est pas fourni, utilise la date de la dernière année comme fallback
   */
  async getGameStates(since?: string): Promise<GameState[]> {
    // Toujours fournir un paramètre since pour optimiser les requêtes
    // Si since n'est pas fourni, utiliser la date de la dernière année comme fallback
    const sinceDate = since || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();
    const params = {since: sinceDate};
    const response = await this.get<GameState[]>('/api/gamestates', {params});
    return response || [];
  }

  /**
   * Crée un gameState sur le serveur
   * Identique à Flutter ApiService.createGameState
   */
  async createGameState(gameState: GameState): Promise<GameState> {
    try {
      const response = await this.post<GameState>('/api/gamestates', gameState);
      return response;
    } catch (error: any) {
      // Si 409 Conflict, le gameState existe déjà, on le récupère
      if (error.response?.status === 409) {
        // Récupérer uniquement les GameStates modifiés depuis la dernière année
        // pour éviter de récupérer tous les GameStates (optimisation)
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        const states = await this.getGameStates(oneYearAgo.toISOString());
        const existing = states.find(
          (s) => s.uniqueURI === gameState.uniqueURI && s.slot === gameState.slot
        );
        return existing || gameState;
      }
      // Sinon, lancer l'erreur (pas de fallback)
      throw error;
    }
  }

  /**
   * Met à jour un gameState sur le serveur
   */
  async updateGameState(gameState: GameState): Promise<GameState> {
    if (!gameState.idApi) {
      throw new Error('Cannot update game state without server ID');
    }
    const response = await this.put<GameState>(`/api/gamestates/${gameState.idApi}`, gameState);
    return response;
  }

  /**
   * Supprime un gameState sur le serveur
   */
  async deleteGameState(idApi: string): Promise<void> {
    await this.delete(`/api/gamestates/${idApi}`);
  }
}

export default new ApiService();
